import { memo, useMemo } from 'react';

import { Button, CopyText, Flex, Heading, Label, Text, TruncatedText } from '@/components/core';
import { ActionRunTest, HowToGuide } from '@/components/flow';
import { WarningDuotoneIcon } from '@/components/icons';
import { PowerAutomateLogo } from '@/components/logos';
import { useGetAppClientWithSecret } from '@/hooks';
import { toActionId, useGetAction } from '@/hooks/api';
import { useTriggerStore } from '@/store';
import { isNotConfiguredExport } from '@/utils';

export type ExportPowerAutomateConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const IExportPowerAutomateConfiguration = ({}: ExportPowerAutomateConfigurationProps = {}) => {
  const action = useTriggerStore((state) => state.action);

  const { data: exportAction } = useGetAction(toActionId(action.actionId), {
    refetchInterval: (query) => (!query.state.data?.config?.url ? 2000 : undefined),
  });

  const { data: appClient } = useGetAppClientWithSecret();

  const clientCredentials = useMemo(
    () => (appClient?.clientSecret ? btoa(`${appClient.clientId}:${appClient.clientSecret}`) : undefined),
    [appClient]
  );

  const isConfigured = useMemo(() => !isNotConfiguredExport(exportAction), [exportAction]);

  return (
    <Flex col className="gap-sm p-md grow">
      <div className="gap-sm flex items-center">
        <Text size="xs" className="font-semibold text-gray-600">
          Account
        </Text>

        <div className="grow" />

        {isConfigured ? (
          <>
            <div className="border-success-100 bg-success-500 size-3 rounded-full border-3" />
            <Text size="xs" className="text-success-700 font-semibold">
              Connected
            </Text>
          </>
        ) : (
          <>
            <div className="size-3 rounded-full border-3 border-gray-100 bg-gray-500" />
            <Text size="xs" className="font-semibold text-gray-500">
              Not connected
            </Text>
          </>
        )}
      </div>

      <Flex className="p-sm gap-sm bg-gray-25 items-center rounded-md border-1 border-gray-100">
        <PowerAutomateLogo className="shrink-0" size="xl" />

        <TruncatedText className="grow-0">
          {isConfigured ? (exportAction?.config?.url as string) : 'Sign in and configure your flow'}
        </TruncatedText>

        {!isConfigured ? (
          <>
            <div className="grow" />
            <Button
              className="shrink-0"
              variant="primary"
              onPress={() => window.open('https://make.powerautomate.com', '_blank')}
            >
              <Text>Sign in</Text>
            </Button>
          </>
        ) : null}
      </Flex>

      {!isConfigured ? (
        <>
          <Flex variant="plain" col className="p-md gap-4 rounded-lg border-1 border-gray-100 bg-white shadow-xs">
            <Flex col className="gap-xs">
              <Label>Client Credentials</Label>
              <CopyText isSensitive value={clientCredentials} />
            </Flex>
          </Flex>

          <Flex variant="warning" className="p-md gap-sm rounded-lg">
            <WarningDuotoneIcon />

            <Flex col className="border-none">
              <Heading size="sm">Setup required</Heading>
              <Text>Make sure your Power Automate flow is saved and configured with Cradl AI.</Text>
            </Flex>
          </Flex>
        </>
      ) : null}

      {isConfigured && <ActionRunTest label="Test connection" />}

      <div className="grow" />

      <HowToGuide actionId={action?.actionId} />
    </Flex>
  );
};

export const ExportPowerAutomateConfiguration = memo(
  IExportPowerAutomateConfiguration
) as typeof IExportPowerAutomateConfiguration;
